import { useEffect, useState } from 'react'
import { MoveRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldLabel,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@campai/lib-web/components/new/ui'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

type Category = RouterOutput['product']['community']['categories']['listCategories']['categories'][number]

type MoveDialogProps = {
    open: boolean
    currentCategoryId: string
    onOpenChange: (open: boolean) => void
    onSubmit: (targetCategoryId: string) => Promise<void>
}

export const MoveDialog = ({ open, currentCategoryId, onOpenChange, onSubmit }: MoveDialogProps) => {
    const { t } = useTranslation()
    const [categories, setCategories] = useState<Category[] | null>(null)
    const [selectedCategoryId, setSelectedCategoryId] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const targetCategories = categories?.filter((category) => category._id.toString() !== currentCategoryId) || []
    const selectedCategory = targetCategories.find((category) => category._id.toString() === selectedCategoryId)

    useEffect(() => {
        if (!open) {
            return
        }

        const abortController = new AbortController()
        setCategories(null)
        setSelectedCategoryId('')
        setIsSubmitting(false)

        void client.product.community.categories.listCategories
            .query(
                {
                    limit: 100,
                    offset: 0,
                    returnCount: false,
                    sort: { title: 'asc' },
                },
                { signal: abortController.signal }
            )
            .then((result) => {
                if (abortController.signal.aborted) {
                    return
                }

                setCategories(result.categories)
                const [firstTargetCategory] = result.categories.filter(
                    (category) => category._id.toString() !== currentCategoryId
                )
                setSelectedCategoryId(firstTargetCategory?._id.toString() || '')
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setCategories([])
                }
            })

        return () => abortController.abort()
    }, [open, currentCategoryId])

    const submit = async () => {
        if (!selectedCategoryId || isSubmitting) {
            return
        }

        setIsSubmitting(true)

        try {
            await onSubmit(selectedCategoryId)
            onOpenChange(false)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('product.community.topic.moveDialog.title')}</DialogTitle>
                    <DialogDescription>{t('product.community.topic.moveDialog.description')}</DialogDescription>
                </DialogHeader>
                <Field>
                    <FieldLabel required>{t('product.community.topic.moveDialog.category')}</FieldLabel>
                    <Select
                        value={selectedCategoryId}
                        onValueChange={(value) => {
                            if (typeof value === 'string') {
                                setSelectedCategoryId(value)
                            }
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue>
                                {selectedCategory?.title || t('product.community.topic.moveDialog.categoryPlaceholder')}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {targetCategories.map((category) => (
                                    <SelectItem key={category._id.toString()} value={category._id.toString()}>
                                        {category.title}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </Field>
                <DialogFooter>
                    <DialogClose
                        render={
                            <Button variant="outline" disabled={isSubmitting}>
                                {t('common.cancel')}
                            </Button>
                        }
                    />
                    <Button
                        loading={isSubmitting}
                        disabled={!selectedCategoryId || !targetCategories.length}
                        onClick={() => void submit()}
                    >
                        <MoveRightIcon data-icon="inline-start" />
                        {t('product.community.topic.moveDialog.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
